import { useState, useEffect } from "react";
import { OnboardingWelcome } from "./OnboardingWelcome";
import { OnboardingTour } from "./OnboardingTour";
import { useAuth } from "@/hooks/useAuth";

type OnboardingStep = "welcome" | "tour" | "completed";

export const OnboardingManager = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("completed");

  useEffect(() => {
    if (user) {
      // Check if user has completed onboarding
      const hasCompletedOnboarding = localStorage.getItem(`onboarding-completed-${user.id}`);
      const hasOptedOut = localStorage.getItem(`onboarding-opted-out-${user.id}`);
      
      console.log('Onboarding check:', {
        userId: user.id,
        hasCompletedOnboarding,
        hasOptedOut
      });
      
      if (!hasCompletedOnboarding && !hasOptedOut) {
        setCurrentStep("welcome");
      } else {
        // Explicitly set to completed if user has opted out or completed
        setCurrentStep("completed");
      }
    }
  }, [user]);

  const completeOnboarding = () => {
    if (user) {
      localStorage.setItem(`onboarding-completed-${user.id}`, "true");
      console.log('Onboarding completed for user:', user.id);
    }
    setCurrentStep("completed");
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const optOutOnboarding = () => {
    if (user) {
      localStorage.setItem(`onboarding-opted-out-${user.id}`, "true");
      localStorage.setItem(`onboarding-completed-${user.id}`, "true");
      console.log('Onboarding opted out for user:', user.id);
      
      // Force immediate save to localStorage
      try {
        localStorage.setItem(`onboarding-opted-out-${user.id}`, "true");
        localStorage.setItem(`onboarding-completed-${user.id}`, "true");
      } catch (error) {
        console.error('Failed to save onboarding preferences:', error);
      }
    }
    setCurrentStep("completed");
  };

  const restartOnboarding = () => {
    if (user) {
      localStorage.removeItem(`onboarding-completed-${user.id}`);
      localStorage.removeItem(`onboarding-opted-out-${user.id}`);
      setCurrentStep("welcome");
    }
  };

  const moveToTour = () => {
    setCurrentStep("tour");
  };

  // Make restartOnboarding available globally for Help page
  useEffect(() => {
    (window as any).restartOnboarding = restartOnboarding;
    return () => {
      delete (window as any).restartOnboarding;
    };
  }, [restartOnboarding]);

  if (currentStep === "completed") {
    return null;
  }

  return (
    <>
      {currentStep === "welcome" && (
        <OnboardingWelcome 
          onComplete={moveToTour}
          onSkip={skipOnboarding}
          onOptOut={optOutOnboarding}
        />
      )}
      
      {currentStep === "tour" && (
        <OnboardingTour 
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
          onOptOut={optOutOnboarding}
        />
      )}
    </>
  );
};